import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Input, Segmented, Empty } from 'antd';
import { Plus, Search, ChevronRight, CalendarClock, CheckCircle2 } from 'lucide-react';
import dayjs from 'dayjs';
import AddForm from './component/AddForm';
import ViewComponent from './component/ViewComponent';
import UserSidebar, { USER_PAGE_ROW } from '../../components/SidebarNew/UserSidebar';
import Layout from '../../Layout';
import { currentUser } from '../Social/socialStore';
import {
  currentRole, doneCount, filterRoadmaps, isCompleted, isReady, progressOf, targetRole, useRoadmaps,
  type Roadmap, type RoadmapFilter,
} from './careerStore';

function RoadmapCard({ r, onOpen }: { r: Roadmap; onOpen: () => void }) {
  const progress = progressOf(r);
  const done = isCompleted(r);
  const ready = isReady(r);
  const overdue = !done && r.targetMonth && dayjs(`${r.targetMonth}-01`).endOf('month').isBefore(dayjs());
  return (
    <button type="button" onClick={onOpen}
      className="text-left bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm hover:border-[#3e91ab] hover:shadow-md transition cursor-pointer group flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full text-white flex items-center justify-center font-bold shrink-0" style={{ background: 'linear-gradient(to bottom right, #f472b6, #e11d48)' }}>
          {r.employee.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-gray-900 dark:text-gray-100 truncate group-hover:text-[#3e91ab]">{r.employee}</div>
          <div className="text-xs text-gray-500 truncate">{r.department}</div>
        </div>
        {done ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 shrink-0">Hoàn thành</span>
          : ready ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300 shrink-0">Sẵn sàng đánh giá</span>
            : overdue ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300 shrink-0">Quá hạn</span> : null}
      </div>
      <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 line-clamp-1">{r.name}</div>
      <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 min-w-0">
        <span className="truncate">{currentRole(r)}</span>
        {!done && <><ChevronRight size={12} className="shrink-0 text-gray-400" /><span className="truncate font-bold text-rose-600">{targetRole(r)}</span></>}
      </div>
      <div>
        <div className="flex justify-between text-[11px] text-gray-500 mb-1">
          <span>{done ? 'Đã đi hết lộ trình' : `Đạt ${doneCount(r)}/${r.criteria.length} điều kiện`}</span>
          <span className="font-bold text-gray-700 dark:text-gray-200">{done ? 100 : progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div className={`h-full rounded-full ${done ? 'bg-blue-500' : ready ? 'bg-green-500' : 'bg-rose-500'}`} style={{ width: `${done ? 100 : progress}%` }} />
        </div>
      </div>
      {r.targetMonth && !done && (
        <div className={`flex items-center gap-1 text-[11px] ${overdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
          <CalendarClock size={12} /> Hạn {dayjs(`${r.targetMonth}-01`).format('MM/YYYY')}
        </div>
      )}
    </button>
  );
}

const CareerProgression = () => {
  const roadmaps = useRoadmaps();
  const me = useMemo(currentUser, []);
  const [params, setParams] = useSearchParams();
  const viewId = params.get('id');
  const editId = params.get('edit');
  const isNew = params.get('mode') === 'new';
  const filter = (params.get('filter') as RoadmapFilter) || 'all';
  const q = params.get('q') || '';

  const go = (patch: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace });
  };
  const toList = () => go({ id: null, edit: null, mode: null });

  const list = filterRoadmaps(roadmaps, filter, q);
  const viewing = viewId ? roadmaps.find(r => r.id === viewId) : null;
  const editing = editId ? roadmaps.find(r => r.id === editId) : null;
  const counts = {
    all: roadmaps.length,
    active: roadmaps.filter(r => !isCompleted(r)).length,
    ready: roadmaps.filter(isReady).length,
    completed: roadmaps.filter(isCompleted).length,
  };

  let body: React.ReactNode;
  if (isNew || editing) {
    body = (
      <AddForm
        key={editing?.id ?? 'new'}
        editing={editing}
        onClose={() => (editing ? go({ edit: null, id: editing.id }, true) : toList())}
        onSaved={id => go({ mode: null, edit: null, id }, true)}
      />
    );
  } else if (viewId) {
    body = viewing
      ? <ViewComponent roadmap={viewing} evaluator={me.name} onBack={toList} onEdit={() => go({ edit: viewing.id, id: null })} onDeleted={() => go({ id: null }, true)} />
      : (
        <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
          <Empty description="Lộ trình không tồn tại hoặc đã bị xoá" />
          <button onClick={toList} className="mt-4 text-sm font-semibold text-[#3e91ab] hover:underline cursor-pointer">← Về danh sách lộ trình</button>
        </div>
      );
  } else {
    body = (
      <div className="flex-1 flex flex-col bg-[#F8FAFC] dark:bg-gray-950">
        <div className="bg-white dark:bg-gray-900 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">Lộ trình thăng tiến</h1>
            <p className="text-xs text-gray-500">{counts.active} lộ trình đang thực hiện{counts.ready ? ` · ${counts.ready} sẵn sàng đánh giá` : ''}</p>
          </div>
          <button onClick={() => go({ mode: 'new' })} className="self-start md:self-auto px-4 py-2 bg-[#3e91ab] text-white rounded-lg text-sm font-semibold hover:bg-[#2d778d] flex items-center gap-1.5 shadow-sm cursor-pointer">
            <Plus size={16} /> Thêm lộ trình mới
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-col 2xl:flex-row gap-3 2xl:items-center justify-between">
            <div className="overflow-x-auto max-w-full -mx-1 px-1">
            <Segmented
              value={filter}
              onChange={v => go({ filter: v === 'all' ? null : String(v) }, true)}
              options={[
                { label: `Tất cả (${counts.all})`, value: 'all' },
                { label: `Đang thực hiện (${counts.active})`, value: 'active' },
                { label: `Sẵn sàng đánh giá (${counts.ready})`, value: 'ready' },
                { label: `Hoàn thành (${counts.completed})`, value: 'completed' },
              ]}
            />
            </div>
            <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm nhân sự, vị trí, phòng ban..."
              defaultValue={q} onChange={e => go({ q: e.target.value || null }, true)} className="2xl:max-w-xs" />
          </div>
          {list.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
              {list.map(r => <RoadmapCard key={r.id} r={r} onOpen={() => go({ id: r.id })} />)}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-10 text-center">
              {roadmaps.length === 0 ? (
                <>
                  <CheckCircle2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">Chưa có lộ trình thăng tiến nào.</p>
                  <button onClick={() => go({ mode: 'new' })} className="px-4 py-2 bg-[#3e91ab] text-white rounded-lg text-sm font-semibold hover:bg-[#2d778d] inline-flex items-center gap-1.5 cursor-pointer"><Plus size={16} /> Thêm lộ trình mới</button>
                </>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có lộ trình nào khớp bộ lọc" />}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Helmet><title>Lộ trình thăng tiến | Khothi</title></Helmet>
      <div className={USER_PAGE_ROW}>
        <UserSidebar />
        <main className="flex-1 min-w-0 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex flex-col overflow-hidden self-start w-full">
          {body}
        </main>
      </div>
    </Layout>
  );
};

export default CareerProgression;
